<template>
  <section class="mb-2 bg-white rounded-md shadow overflow-auto">
    <div class="relative">
      <div class="items-center w-full h-full place-content-center flex">
        <div class="my-6">
          <div class="flex items-center">
            <span class="block cost-total-title">{{ getTripCostFormat }} </span>
            <img
              src="@/assets/icon/info.svg"
              class="cursor-pointer ml-1"
              @click="onPricingPlanHistoryModalReq()"
            />
          </div>
        </div>
      </div>
      <template v-if="isRefunded">
        <div
          v-if="detail.invoice.finalized_refund_status_code === 'FR'"
          class="absolute"
          style="right: 5%; top: 5%"
        >
          <img src="@/assets/img/payment/refunded-seal.png" class="w-32 h-32" />
        </div>
        <div
          v-if="detail.invoice.finalized_refund_status_code === 'PR'"
          class="absolute"
          style="right: 5%; top: 5%"
        >
          <img
            src="@/assets/img/payment/partial_refunded_seal.png"
            class="w-32"
          />
        </div>
      </template>
      <TripNotesView :data="detail" />
    </div>

    <div class="row">
      <div class="grid h-24 grid-cols-2">
        <div
          class="pl-10 place-content-center cost-grid-item border-right flex-col text-left"
        >
          <div class="cost-title-no-color text-gray-500">
            {{ items[0].title }}
          </div>
          <div class="cost-title-no-color text-oDark">
            {{ items[0].subTitle }}
          </div>
        </div>
        <div
          class="pr-10 place-content-center cost-grid-item flex-col text-right"
        >
          <div class="cost-title-no-color text-gray-500">
            {{ items[1].title }}
          </div>
          <div class="cost-title-no-color text-oDark">
            {{ items[1].subTitle }}
          </div>
        </div>
      </div>
    </div>

    <div class="px-6">
      <div
        class="cost-list"
        v-for="(category, index) in getCostCategories"
        :key="index"
      >
        <div
          :class="{
            'col-w-6': true,
            'flex':
              category.id === 'discount' ||
              category.id === 'reward_zone_discount',
          }"
        >
          <span
            :class="{
              'cost-title': true,
              'flex-grow':
                category.id === 'discount' ||
                category.id === 'reward_zone_discount',
            }"
            :style="
              category.id === 'discount' ||
              category.id === 'reward_zone_discount'
                ? 'color:#3ebc30'
                : 'color:#000000'
            "
            >{{ category.name }}</span
          >
          <span
            class="cost-title ml-5"
            v-if="
              category.id === 'discount' &&
                (detail.applied_pass ||
                  detail.applied_promo ||
                  detail.applied_voucher)
            "
          >
            <span class="victory-text-alt" :style="`color: ${getColor};`">
              <RouterLink
                v-if="detail.applied_voucher"
                :to="{
                  name: 'VoucherDetails',
                  params: { id: detail.applied_voucher.id },
                }"
                target="_blank"
              >
                {{ discountDescription }}
              </RouterLink>
              <RouterLink
                v-if="detail.applied_promo"
                :to="{
                  name: 'PromotionDetails',
                  params: { id: detail.applied_promo.id },
                }"
                target="_blank"
              >
                {{ discountDescription }}
              </RouterLink>
              <RouterLink
                v-if="detail.applied_pass"
                :to="{
                  name: 'PassDetails',
                  params: { id: detail.applied_pass.id },
                }"
                target="_blank"
              >
                {{ discountDescription }}
              </RouterLink>
            </span>
          </span>
          <span
            class="rider-type-badge host"
            v-if="
              category.id === 'trip_cost' &&
                !category.isChild &&
                detail.child_trips &&
                detail.child_trips.length > 0
            "
            >{{ $t('components.tripManagement.details.cost.host') }}</span
          >
          <span
            class="rider-type-badge guest"
            v-if="category.id === 'trip_cost' && category.isChild"
            >{{ $t('components.tripManagement.details.cost.guest') }}</span
          >
          <p
            v-if="category.id === 'trip_cost'"
            class="font-normal text-gray-700 text-xs"
          >
            {{ getTripCostSubText(detail) }}
          </p>
          <p
            v-if="category.id === 'pause_time_cost'"
            class="font-normal text-gray-700 text-xs"
          >
            {{ getPauseCostSubText(detail) }}
          </p>
          <p
            v-if="category.id === 'penalty_amount' && getPenalties.length > 0"
            class="font-normal text-gray-700 text-xs"
          >
            {{ getPenaltySubText(detail) }}
          </p>
        </div>
        <div class="justify-end text-right col-w-4">
          <span
            class="cost-val regular-cost"
            v-if="hasDiscountedPrice(category.id)"
            >{{ category.regularPrice }}</span
          >

          <span
            class="cost-val"
            :style="
              category.id == 'discount' || category.id == 'reward_zone_discount'
                ? 'color:#3ebc30'
                : 'color:#000000'
            "
            >{{ category.price }}</span
          >
        </div>
      </div>

      <!-- <div
        :class="{
          'cost-list': true,
          'no-border': !detail.notes || detail.notes.length < 1,
        }"
      >
        <div class="col-w-7">
          <i class="fas fa-credit-card"></i>
          <span class="ml-3 cost-title">Payment</span>
        </div>
        <div class="col-w-3" style="text-align: end">
          <span class="cost-val">
            {{ getPaymentMethod }}
          </span>
        </div>
      </div> -->

      <div>
        <div class="cost-list no-border ">
          <p class="font-bold text-lg">
            {{ $t('components.tripManagement.details.cost.payments') }}
          </p>
        </div>
      </div>

      <div>
        <div
          v-for="(paymentRecord, paymentRecordIndex) in getPaymentRecords"
          :key="paymentRecordIndex"
        >
          <div class="cost-list no-border">
            <div class="col-w-7">
              <p>{{ paymentRecord.payment_source_name }}</p>
              <p
                v-if="paymentRecord.payment_note"
                class="text-sm text-gray-500"
              >
                {{ paymentRecord.payment_note }}
              </p>
            </div>
            <div class="col-w-3" style="text-align: end">
              <p>
                <span v-if="paymentRecord.is_for_refund">-</span>
                {{ getFormattedPrice(paymentRecord.amount) }}
              </p>
              <p
                v-if="paymentRecord.is_for_refund"
                class="text-sm text-gray-500"
              >
                {{ $t('components.tripManagement.details.cost.refunded') }}
              </p>
            </div>
            <div
              v-if="
                paymentRecord.payment_source_type === 'card' &&
                  detail.invoice.payment_method
              "
              class="pl-1 cursor-pointer"
              @click="togglePaymentDetailsView(paymentRecordIndex)"
            >
              <i
                v-if="paymentRecord.isShowDetails"
                class="text-sm fa fa-chevron-up text-blue-500"
              />
              <i v-else class="text-sm fa fa-chevron-down text-blue-500" />
            </div>
          </div>
          <AccordionTransition
            :expanded="paymentRecord.isShowDetails"
            v-if="detail.invoice.payment_method"
          >
            <div class="px-5 text-sm text-gray-500">
              <div
                v-if="detail.invoice.payment_method.id"
                class="flex justify-between"
              >
                <p>
                  {{ $t('components.tripManagement.details.cost.id') }}
                </p>
                <p>{{ detail.invoice.payment_method.id }}</p>
              </div>
              <div
                v-if="detail.invoice.payment_method.brand"
                class="flex justify-between my-1"
              >
                <p>
                  {{ $t('components.tripManagement.details.cost.cardType') }}
                </p>
                <p>
                  {{ detail.invoice.payment_method.brand }}
                </p>
              </div>
              <div
                v-if="detail.invoice.payment_method.expiry"
                class="flex justify-between my-1"
              >
                <p>
                  {{ $t('components.tripManagement.details.cost.expireAt') }}
                </p>
                <p>
                  {{
                    getFormattedDateTime(
                      detail.invoice.payment_method.expiry,
                      'D MMM YYYY'
                    )
                  }}
                </p>
              </div>
              <div
                v-if="detail.invoice.payment_method.user.full_name"
                class="flex justify-between my-1"
              >
                <p>
                  {{ $t('components.tripManagement.details.cost.owner') }}
                </p>
                <p>
                  {{ detail.invoice.payment_method.user.full_name }}
                </p>
              </div>
              <div
                v-if="detail.invoice.payment_method.extra_detail"
                class="flex justify-between my-1"
              >
                <p>
                  {{ $t('components.tripManagement.details.cost.email') }}
                </p>
                <p>
                  {{ detail.invoice.payment_method.extra_detail }}
                </p>
              </div>
              <div
                v-if="detail.invoice.payment_method.cvc_check"
                class="flex justify-between my-1"
              >
                <p>
                  {{ $t('components.tripManagement.details.cost.cvcCheck') }}
                </p>
                <p>
                  {{ detail.invoice.payment_method.cvc_check }}
                </p>
              </div>
              <div
                v-if="detail.invoice.payment_method.card_origin_country"
                class="flex justify-between my-1"
              >
                <p>
                  {{
                    $t('components.tripManagement.details.cost.originCountry')
                  }}
                </p>
                <p>
                  {{ detail.invoice.payment_method.card_origin_country }}
                </p>
              </div>
            </div>
          </AccordionTransition>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
// import TripMultiIcon from './TripMultiIcon.vue'
// import CostMessageBox from '@/composites/trip/shared/CostMessageBox'
import AccordionTransition from '@/components/accordion/AccordionTransition.vue'
import {
  getFormattedDateTime,
  getHumanReadableDuration,
  convertSecondsToMinutesAndSeconds,
} from '@/utils/datetime'

// import OtoDrowDownIcon from "@/components/ui/OtoDrowDownIcon";
import { getFormattedDate } from '@/utils/datetime'

const PASS_TYPES = {
  MM: 'Monthly',
  YY: 'Yearly',
  DD: 'Daily',
  WE: 'Weekly',
}

// function getTaxFormattedTitle(taxRate) {
//   if (taxRate) {
//     return `VAT ${parseFloat(taxRate).toFixed(0)}%`
//   }

//   return 'VAT'
// }

export default {
  components: {
    // TripMultiIcon,
    // CostMessageBox,
    //OtoDrowDownIcon
    AccordionTransition,
    TripNotesView: () =>
      import('@/composites/trip/details/blocks/timeline/TripNotesView.vue'),
  },
  props: {
    detail: {
      required: false,
      default: null,
    },
    symbol: {
      required: false,
    },
  },
  mounted() {
    console.log('detail = ', this.detail)

    this.items.forEach((item) => {
      if (item.id === 'transactionTime') {
        item.subTitle = this.detail.invoice
          ? getFormattedDateTime(this.detail.invoice.date, 'D MMM YYYY, h:mm a')
          : '--'
      } else if (item.id === 'invoiceID') {
        item.subTitle = this.detail.invoice
          ? `#${this.detail.invoice.id.substr(
              this.detail.invoice.id.length - 5
            )}`
          : '--'
      }
    })

    for (let i = 0; i < this.getCostCategories.length; ++i) {
      if (this.getCostCategories[i].id == 'discount') {
        this.getCostCategories[i].price = this.getFormattedPrice(
          this.detail.discount
        )
      } else if (this.getCostCategories[i].id == 'reward_zone_discount') {
        this.getCostCategories[i].name = `${
          this.getCostCategories[i].name
        } (${parseFloat(this.detail.reward_zone_discount_percentage)}%)`

        this.getCostCategories[i].price = this.getFormattedPrice(
          this.detail.reward_zone_discount
        )
      } else if (this.getCostCategories[i].id == 'unlock_charge') {
        let totalPrice = this.detail.unlock_charge
        let originalTotalPrice = this.detail.original_unlock_cost

        if (this.detail.child_trips) {
          for (let i = 0; i < this.detail.child_trips.length; ++i) {
            totalPrice += this.detail.child_trips[i].unlock_charge
            originalTotalPrice += this.detail.child_trips[i]
              .original_unlock_cost
          }
        }

        this.getCostCategories[i].price = this.getFormattedPrice(totalPrice)

        if (this.detail.original_unlock_cost) {
          this.getCostCategories[i].regularPrice = this.getFormattedPrice(
            originalTotalPrice
          )
        }
      } else if (this.getCostCategories[i].id == 'reservation_charge') {
        let totalPrice = this.detail.reservation_charge
        let originalTotalPrice = this.detail.original_reservation_cost

        if (this.detail.child_trips) {
          for (let i = 0; i < this.detail.child_trips.length; ++i) {
            totalPrice += this.detail.child_trips[i].reservation_charge
            originalTotalPrice += this.detail.child_trips[i]
              .original_reservation_cost
          }
        }

        this.getCostCategories[i].price = this.getFormattedPrice(totalPrice)

        if (this.detail.original_reservation_cost) {
          this.getCostCategories[i].regularPrice = this.getFormattedPrice(
            originalTotalPrice
          )
        }
      } else if (this.getCostCategories[i].id == 'trip_cost') {
        this.getCostCategories[i].name = this.getTripCostName(
          this.getCostCategories[i].id,
          this.detail
        )
        if (this.detail.riding_time_balance_used !== null) {
          this.getCostCategories[i].price = this.getTripCostFormat
        } else {
          this.getCostCategories[i].price = this.getFormattedPrice(
            this.detail.cost
          )
        }

        if (this.detail.child_trips) {
          for (let j = 0; j < this.detail.child_trips.length; j++, i++) {
            // this.cost_categories.push(this.cost_categories[i + j + 1])
            this.getCostCategories.splice(i + 1, 0, {
              id: 'trip_cost',
              name: this.getTripCostName(
                this.getCostCategories[i].id,
                this.detail.child_trips[j]
              ),
              price:
                this.detail.riding_time_balance_used !== null
                  ? '--'
                  : this.getFormattedPrice(this.detail.child_trips[j].cost),
              regularPrice: '0',
              isChild: true,
            })
          }
        }
      } else if (this.getCostCategories[i].id == 'pause_time_cost') {
        this.getCostCategories[i].name = this.getPauseTimeCostName(
          this.getCostCategories[i].id,
          this.detail
        )
        this.getCostCategories[i].price = this.getFormattedPrice(
          this.detail.pause_cost
        )
      } else if (this.getCostCategories[i].id == 'parking_fee') {
        this.getCostCategories[i].price = this.getFormattedPrice(
          this.detail.illegal_parking_charge
        )
      } else if (this.getCostCategories[i].id == 'service_area_exit_fee') {
        this.getCostCategories[i].price = this.getFormattedPrice(
          this.detail.service_area_exit_charge
        )
      } else if (this.getCostCategories[i].id == 'premium_parking_fee') {
        this.getCostCategories[i].price = this.getFormattedPrice(
          this.detail.premium_parking_fee_amount
        )
      } else if (this.getCostCategories[i].id == 'tourist_surcharge') {
        this.getCostCategories[i].name = `${this.$t(
          'components.tripManagement.details.cost.touristSurcharge'
        )} (${parseFloat(this.detail.non_local_rider_charge_percentage).toFixed(
          0
        )}%)`

        this.getCostCategories[i].price = this.getFormattedPrice(
          this.detail.non_local_rider_charge_amount
        )
      } else if (this.getCostCategories[i].id == 'city_fee') {
        this.getCostCategories[i].name =
          this.detail.city_fee_percentage === null
            ? this.$t('components.tripManagement.details.cost.cityFee')
            : `${this.$t(
                'components.tripManagement.details.cost.cityFee'
              )} (${parseFloat(this.detail.city_fee_percentage).toFixed(0)}%)`

        this.getCostCategories[i].price = this.getFormattedPrice(
          this.detail.city_fee_amount
        )
      } else if (this.getCostCategories[i].id == 'tax_amount') {
        this.getCostCategories[i].price = this.getFormattedPrice(
          this.detail.tax_amount
        )

        this.getCostCategories[i].name = this.getTaxFormattedTitle(
          this.detail.tax_rate
        )
      } else if (this.getCostCategories[i].id == 'sub_total') {
        let totalPrice = this.detail.cost
        let totalUnlockPrice = this.detail.unlock_charge
        let totalReservationPrice = this.detail.reservation_charge
        let totalPauseCost = this.detail.pause_cost
        let illegalParkingCharge = this.detail.illegal_parking_charge || 0
        let touristSurcharge =
          parseFloat(this.detail.non_local_rider_charge_amount) || 0
        let cityFee = parseFloat(this.detail.city_fee_amount) || 0

        let premiumParkingFee = this.detail.premium_parking_fee_amount || 0

        if (this.detail.child_trips) {
          for (let i = 0; i < this.detail.child_trips.length; ++i) {
            totalPrice += this.detail.child_trips[i].cost
            totalUnlockPrice += this.detail.child_trips[i].unlock_charge
            totalReservationPrice += this.detail.child_trips[i]
              .reservation_charge
          }
        }
        this.getCostCategories[i].price = this.getFormattedPrice(
          totalPrice +
            totalUnlockPrice +
            totalReservationPrice +
            totalPauseCost +
            illegalParkingCharge +
            touristSurcharge +
            cityFee +
            premiumParkingFee
        )
      } else if (this.getCostCategories[i].id == 'penalty_amount') {
        this.getCostCategories[i].price = this.getFormattedPrice(
          parseFloat(this.getPenalties[0].deduct_amount).toFixed(2)
        )
      }

      if (
        this.detail.riding_time_balance_used !== null &&
        this.getCostCategories[i].id !== 'trip_cost'
      ) {
        this.getCostCategories[i].price = '--'
      }
    }
    console.log('cost', this.cost_categories)
  },
  computed: {
    getPaymentRecords() {
      if (this.detail.payment_records) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.detail.payment_records = this.detail.payment_records.map((pr) => {
          if (pr.payment_source_type === 'card') {
            return {
              ...pr,
              isShowDetails: false,
            }
          } else {
            return pr
          }
        })

        return this.detail.payment_records
      } else {
        return []
      }
    },
    getCostCategories() {
      return this.getActualCategories()
    },
    getTotalAmount() {
      if (this.detail.invoice) {
        return this.detail.invoice.amount === 0
          ? '-.--'
          : parseFloat(this.detail.invoice.amount).toFixed(2)
      } else {
        return '-.--'
      }
    },
    getTripCostFormat() {
      if (this.detail.riding_time_balance_used !== null) {
        return this.convertSecondsToMinutesAndSeconds(
          this.detail.riding_time_balance_used
        )
      } else {
        return `${this.detail.currency.symbol} ${this.getTotalAmount}`
      }
    },
    getColor() {
      if (this.detail.pass_applied) {
        return `#f07300`
      }
      if (this.detail.promo_applied) {
        return `#8200ff`
      }
      if (this.detail.voucher_applied) {
        return `#0fb400`
      }
      return `#000000`
    },
    promoDescription() {
      const promo = this.detail.applied_promo
      const expiry = getFormattedDate(promo.end_date, 'D MMM, YYYY')
      let message = ''

      if (promo.promotion_category === 'RD') {
        message = `${parseFloat(promo.percentage).toFixed(0)}% off`
      } else if (promo.promotion_category === 'FR') {
        message = 'Free ride'
      } else if (promo.promotion_category === 'FD') {
        message = `${this.currencySymbol}${parseFloat(
          promo.flat_discount_amount
        ).toFixed(0)} off`
      }

      return `${message}. Valid till ${expiry}.`
    },
    passDescription() {
      const pass = this.detail.applied_pass
      const passPackage = pass.package
      const title = `${PASS_TYPES[passPackage.period]} Pass`
      const expiry = getFormattedDate(passPackage.end_date, 'D MMM, YYYY')
      return `${title}. Valid till ${expiry}.`
    },
    voucherDescription() {
      const voucher = this.detail.applied_voucher
      const title = `${voucher.package.description ||
        voucher.package.title ||
        ''}`
      const expiry = getFormattedDate(voucher.expire_date, 'D MMM, YYYY')
      return `${title}. Valid till ${expiry}.`
    },
    discountDescription() {
      const promo = this.detail.applied_promo

      if (promo) {
        return `${promo.code}`
      }

      const voucher = this.detail.applied_voucher

      if (voucher) {
        return `${voucher.package.title}`
      }

      const pass = this.detail.applied_pass

      if (pass) {
        const passPackage = pass.package
        const title = `${PASS_TYPES[passPackage.period]} Pass`
        return `${title}`
      }

      return ''
    },
    currencySymbol() {
      return this.detail?.currency?.symbol || this.$org.default_currency.symbol
    },
    isRefunded() {
      return this.detail?.invoice && this.detail.invoice.is_refunded
    },
    getPaymentMethod() {
      const sources = []

      if (!this.detail.invoice) {
        sources.push('eWallet')
      } else {
        const paymentMethod = this.detail.invoice?.payment_method

        if (paymentMethod) {
          sources.push(`${paymentMethod.brand} ${paymentMethod.number}`)
        } else {
          sources.push('eWallet')
        }
      }

      if (this.detail.subscription_charge_amount !== null) {
        sources.push('Pass')
      }

      // if (this.detail.applied_promo && !this.detail.invoice) {
      //   sources.push('Promotion')
      // }

      // if (this.detail.applied_voucher && !this.detail.invoice) {
      //   sources.push('Voucher')
      // }

      return sources.join(', ')
    },
    getPenalties() {
      const penalties = []
      if (this.detail?.penalties) {
        this.detail.penalties.forEach((penalty) => {
          if (
            parseFloat(this.detail.service_area_exit_charge) > 0 &&
            penalty.title === 'Went Outside Geofence'
          ) {
            return
          }
          if (
            parseFloat(this.detail.illegal_parking_charge) > 0 &&
            penalty.title === 'Illegal Parking'
          ) {
            return
          }
          penalties.push(penalty)
        })
      }
      return penalties
    },
  },
  data() {
    return {
      onTripDurationStr: null,
      cost_categories: [
        {
          id: 'reservation_charge',
          name: this.$t('components.tripManagement.details.cost.reservation'),
          price: '0',
          regularPrice: '0',
        },
        {
          id: 'unlock_charge',
          name: this.$t('components.tripManagement.details.cost.unlockCharge'),
          price: '0',
          regularPrice: '0',
        },
        {
          id: 'trip_cost',
          name: this.$t('components.tripManagement.details.cost.tripCost'),
          price: '0',
          regularPrice: '0',
        },
        {
          id: 'pause_time_cost',
          name: this.$t('components.tripManagement.details.cost.pauseTimeCost'),
          price: '0',
        },
        {
          id: 'parking_fee',
          name: this.$t('components.tripManagement.details.cost.parkingFee'),
          price: '0',
        },
        {
          id: 'service_area_exit_fee',
          name: this.$t(
            'components.tripManagement.details.cost.serviceAreaExitFee'
          ),
          price: '0',
        },
        {
          id: 'tourist_surcharge',
          name: this.$t(
            'components.tripManagement.details.cost.touristSurcharge'
          ),
          price: '0',
        },
        {
          id: 'city_fee',
          name: this.$t('components.tripManagement.details.cost.cityFee'),
          price: '0',
        },
        {
          id: 'premium_parking_fee',
          name: this.$t(
            'components.tripManagement.details.cost.premiumParkingFee'
          ),
          price: '0',
        },
        {
          id: 'sub_total',
          name: this.$t('components.tripManagement.details.cost.subTotal'),
          price: '0',
        },
        {
          id: 'discount',
          name: this.$t('components.tripManagement.details.cost.discount'),
          price: '0',
        },
        {
          id: 'reward_zone_discount',
          name: this.$t(
            'components.tripManagement.details.cost.rewardZoneDiscount'
          ),
          price: '0',
        },
        {
          id: 'tax_amount',
          name: this.$t('components.tripManagement.details.cost.vat'),
          price: '0',
        },
        {
          id: 'penalty_amount',
          name: this.$t(
            'components.tripManagement.details.cost.appliedPenaltyAmount'
          ),
          price: '0.00',
        },
      ],
      items: [
        {
          id: 'transactionTime',
          icon_class: 'fas fa-thumbs-up category-icon',
          title: this.$t(
            'components.tripManagement.details.cost.transactionTime'
          ),

          color: '#3ebc30',
        },
        {
          id: 'invoiceID',
          icon_class: 'fas fa-road category-icon',
          title: this.$t('components.tripManagement.details.cost.invoiceId'),
        },
      ],
    }
  },
  methods: {
    getFormattedDateTime,
    convertSecondsToMinutesAndSeconds,

    togglePaymentDetailsView(index) {
      this.detail.payment_records[index].isShowDetails = !this.detail
        .payment_records[index].isShowDetails
    },
    getActualCategories() {
      let categories = this.cost_categories
      if (this.detail.reservation_charge == 0) {
        categories = categories.filter(
          (item) => item.id !== 'reservation_charge'
        )
      }
      if (this.getPenalties.length == 0) {
        categories = categories.filter((item) => item.id !== 'penalty_amount')
      }
      if (this.detail.pause_time_secs_till_now == 0) {
        categories = categories.filter((item) => item.id !== 'pause_time_cost')
      }
      if (this.detail.illegal_parking_charge == 0) {
        categories = categories.filter((item) => item.id !== 'parking_fee')
      }
      if (parseFloat(this.detail.service_area_exit_charge) === 0) {
        categories = categories.filter(
          (item) => item.id !== 'service_area_exit_fee'
        )
      }
      if (parseFloat(this.detail.premium_parking_fee_amount) == 0) {
        categories = categories.filter(
          (item) => item.id !== 'premium_parking_fee'
        )
      }
      if (this.detail.non_local_rider_charge_amount == 0) {
        categories = categories.filter(
          (item) => item.id !== 'tourist_surcharge'
        )
      }
      if (this.detail.city_fee_amount == 0) {
        categories = categories.filter((item) => item.id !== 'city_fee')
      }
      if (this.detail.reward_zone_discount_percentage === null) {
        categories = categories.filter(
          (item) => item.id !== 'reward_zone_discount'
        )
      }
      if (this.detail.discount == 0) {
        categories = categories.filter((item) => item.id !== 'discount')
      }
      return categories
    },
    onPricingPlanHistoryModalReq() {
      this.$modal.show('pricingPlanHistoryModal')
    },
    getFormattedPrice(rawPrice) {
      let _price = rawPrice

      if (typeof rawPrice !== 'number') {
        _price = parseFloat(rawPrice).toFixed(2)

        if (isNaN(_price)) {
          return '--'
        }
      } else {
        _price = rawPrice.toFixed(2)
      }

      return `${this.detail.currency.symbol} ${_price}`
    },
    getTaxFormattedTitle(taxRate) {
      if (taxRate) {
        return `${this.$t(
          'components.tripManagement.details.cost.vat'
        )} (${parseFloat(taxRate).toFixed(0)}%)`
      }

      return this.$t('components.tripManagement.details.cost.vat')
    },
    getTripsRate(trip) {
      if (trip.riding_time > trip.billing_unit_secs) {
        return this.getFormattedPrice(trip.rate_per_minute)
      } else {
        return this.getFormattedPrice(trip.first_unit_fees)
      }
    },
    getTripCostName(costId, trip) {
      if (costId !== 'trip_cost') {
        return '--'
      }
      if (this.detail.riding_time_balance_used !== null) {
        return this.$t('components.tripManagement.details.cost.tripCost')
      }

      return trip.riding_time &&
        trip.rate_per_minute !== null &&
        trip.rate_per_minute >= 0
        ? this.$t('components.tripManagement.details.cost.tripCostWithValue', {
            time: getHumanReadableDuration(trip.riding_time),
          })
        : this.$t('components.tripManagement.details.cost.tripCost')
    },
    getTripCostSubText(trip) {
      if (this.detail.riding_time_balance_used !== null) {
        return
      }

      return this.$t(
        'components.tripManagement.details.cost.tripCostSubtitle',
        {
          currencySymbol: this.currencySymbol,
          firstUnitFee: trip.first_unit_fees,
          firstUnitFeeUnit:
            trip.billing_unit_secs / 60 === 1
              ? this.$t('components.tripManagement.details.cost.minute')
              : `${trip.billing_unit_secs / 60}${this.$t(
                  'components.tripManagement.details.cost.minute'
                )}`,
          nextUnitFee: trip.rate_per_minute,
          nextUnitFeeUnit:
            trip.billing_unit_secs / 60 === 1
              ? this.$t('components.tripManagement.details.cost.minute')
              : `${trip.billing_unit_secs / 60}${this.$t(
                  'components.tripManagement.details.cost.minute'
                )}`,
        }
      )
    },
    getPenaltySubText(trip) {
      return trip.penalties[0].title
    },
    getPauseTimeCostName(costId, trip) {
      if (costId !== 'pause_time_cost') {
        return '--'
      }
      if (this.detail.riding_time_balance_used !== null) {
        return this.$t('components.tripManagement.details.cost.pauseTimeCost')
      }
      return trip.pause_time_secs_till_now &&
        trip.pause_rate_per_minute !== null &&
        trip.pause_entries.length > 0
        ? this.$t(
            'components.tripManagement.details.cost.pauseTimeCostWithValue',
            {
              time: getHumanReadableDuration(trip.pause_time_secs_till_now),
            }
          )
        : this.$t('components.tripManagement.details.cost.pauseTimeCost')
    },
    getPauseCostSubText(trip) {
      if (this.detail.riding_time_balance_used !== null) {
        return
      }
      return this.$t(
        'components.tripManagement.details.cost.pauseTimeCostSubtitle',
        {
          currencySymbol: this.currencySymbol,
          pauseFirstUnitFee: trip.pause_first_unit_fees,
          pauseFirstUnitFeeUnit:
            trip.pause_billing_unit_mins === 1
              ? this.$t('components.tripManagement.details.cost.minute')
              : `${trip.pause_billing_unit_mins}${this.$t(
                  'components.tripManagement.details.cost.minute'
                )}`,
          nextUnitFee: trip.pause_rate_per_minute,
          nextUnitFeeUnit:
            trip.pause_billing_unit_mins === 1
              ? this.$t('components.tripManagement.details.cost.minute')
              : `${trip.pause_billing_unit_mins}${this.$t(
                  'components.tripManagement.details.cost.minute'
                )}`,
        }
      )
    },

    hasDiscountedPrice(categoryId) {
      if (
        categoryId === 'unlock_charge' &&
        this.detail.original_unlock_cost &&
        this.detail.applied_pass
      ) {
        return true
      }

      if (
        categoryId === 'reservation_charge' &&
        this.detail.original_reservation_cost &&
        this.detail.applied_pass
      ) {
        return true
      }

      // if (categoryId === 'unlock_charge' && this.detail.original_unlock_cost) {
      //   return true
      // }

      return false
    },
    // getFormattedPrice(price) {
    //   return parseFloat(price) >= 0
    //     ? this.detail?.currency?.symbol + ' ' + parseFloat(price).toFixed(2)
    //     : '--'
    // },
    getDate(date) {
      var time = new Date(date)

      var month = time.getMonth()
      var days = time.getDate()
      var year = time.getFullYear()
      var arr_month = new Array()
      arr_month[0] = 'January'
      arr_month[1] = 'February'
      arr_month[2] = 'March'
      arr_month[3] = 'April'
      arr_month[4] = 'May'
      arr_month[5] = 'June'
      arr_month[6] = 'July'
      arr_month[7] = 'August'
      arr_month[8] = 'September'
      arr_month[9] = 'October'
      arr_month[10] = 'November'
      arr_month[11] = 'December'

      return days + ' ' + arr_month[month] + ' , ' + year
    },
  },
}
</script>

<style lang="scss" scoped>
.rider-type-badge {
  @apply bg-blue-600;

  color: #fff;
  font-weight: 400 !important;
  font-size: 9.75px !important;
  text-transform: uppercase;
  padding: 2px 6px;
  border-radius: 8px;
  margin-left: 15px !important;

  &.guest {
    @apply bg-gray-400;
  }
}

.notes-wrapper {
  font-family: 'Roboto';
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: left;
  color: #000000;
}

.victory-text-alt {
  font-size: 16px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  font-family: 'Roboto';
  width: 100%;
  text-decoration: underline;
}
</style>
